import { useTranslations } from '@vocab/react';
import {
  Autosuggest,
  filterSuggestions,
  IconSearch,
} from 'braid-design-system';
import { useEffect, useMemo, useState } from 'react';

import { makePartnerSuggestions } from 'src/pages/ConnectedPartners/components/SearchFilter/makePartnerSuggestions';
import translations from 'src/pages/LinkYourSoftware/.vocab';
import type { Partner } from 'src/types';
import { useIntegrationPartners } from 'src/utils/hooks/useIntegrationPartners';
import { chooseConnectedPartners } from 'src/utils/partners';

interface AutosuggestValue {
  text: string;
  value?: Partner;
}

export const SelectPartnerFilter = () => {
  const { partners, selectedPartner, setSelectedPartner } =
    useIntegrationPartners();
  const releaseNotesPartners = chooseConnectedPartners(partners);
  const partnerSuggestions = useMemo(
    () =>
      makePartnerSuggestions(
        releaseNotesPartners.state === 'data' ? releaseNotesPartners.data : [],
      ),
    [releaseNotesPartners],
  );
  const [value, setValue] = useState<AutosuggestValue>({ text: '' });
  const { t } = useTranslations(translations);

  const onSearchValueChange = (newSuggestVal: AutosuggestValue) => {
    setValue(newSuggestVal);
    if (newSuggestVal.value) {
      setSelectedPartner(newSuggestVal.value);
    }
  };

  const clearSearch = () => {
    setValue({ text: '' });
  };

  /**
   * Update the search value when the query changes.
   * This keeps the search value up to date when using browser back/forward buttons.
   */
  useEffect(() => {
    setValue({ text: selectedPartner?.name || '' });
  }, [selectedPartner?.name]);

  return (
    <Autosuggest<Partner>
      id="lys-partner-autosuggest"
      type="search"
      label={t('provider')}
      placeholder={t('selectProvider')}
      icon={<IconSearch />}
      value={value}
      onClear={clearSearch}
      onChange={onSearchValueChange}
      suggestions={filterSuggestions(partnerSuggestions, value.text)}
      automaticSelection={true}
      noSuggestionsMessage={{
        title: t('noResultsFound'),
        description: '',
      }}
    />
  );
};
